import type { NormalizedRecord } from "./types";

export interface FilterState {
  keyword: string;
  category: string;
  theme: string;
  status: string;
}

export const EMPTY_FILTERS: FilterState = {
  keyword: "",
  category: "",
  theme: "",
  status: "",
};

/**
 * Apply text / category / theme / status filters to the full record set.
 * Spec §3.5: case-insensitive substring on content; exact match on the others.
 * The empty status string means "全部" and clears the status constraint.
 */
export function applyFilters(
  records: NormalizedRecord[],
  filters: FilterState,
): NormalizedRecord[] {
  const keyword = filters.keyword.trim().toLowerCase();
  return records.filter((record) => {
    if (keyword && !record.content.toLowerCase().includes(keyword)) {
      return false;
    }
    if (filters.category && record.category !== filters.category) {
      return false;
    }
    if (filters.theme && record.theme !== filters.theme) {
      return false;
    }
    if (filters.status && record.status !== filters.status) {
      return false;
    }
    return true;
  });
}

interface CategoryGroup {
  category: string;
  themes: ThemeGroup[];
}

interface ThemeGroup {
  theme: string;
  records: NormalizedRecord[];
}

/**
 * Group records into "类别 → 主题 → 记录" structure.
 * Spec §3.5: empty category shows as 未设类别; empty theme shows as 未设主题.
 */
export function groupRecords(
  records: NormalizedRecord[],
  finalized = false,
  categoryOrder: string[] = [],
): CategoryGroup[] {
  const byCategory = new Map<string, Map<string, NormalizedRecord[]>>();
  for (const record of records) {
    const category = record.category.trim() || "未设类别";
    const theme = record.theme.trim() || "未设主题";
    let themes = byCategory.get(category);
    if (!themes) {
      themes = new Map();
      byCategory.set(category, themes);
    }
    let bucket = themes.get(theme);
    if (!bucket) {
      bucket = [];
      themes.set(theme, bucket);
    }
    bucket.push(record);
  }
  const groups: CategoryGroup[] = [];
  for (const [category, themes] of byCategory.entries()) {
    const sortedThemes = Array.from(themes.entries())
      .sort(([a], [b]) => a.localeCompare(b, "zh-Hans-CN"))
      .map(([theme, items]) => ({
        theme,
        records: sortRecords(items, finalized),
      }));
    groups.push({ category, themes: sortedThemes });
  }
  const categoryRank = new Map(categoryOrder.map((category, index) => [category, index]));
  groups.sort((a, b) => {
    const aRank = categoryRank.get(a.category);
    const bRank = categoryRank.get(b.category);
    if (aRank !== undefined || bRank !== undefined) {
      if (aRank === undefined) return 1;
      if (bRank === undefined) return -1;
      return aRank - bRank;
    }
    return a.category.localeCompare(b.category, "zh-Hans-CN");
  });
  return groups;
}

function sortRecords(records: NormalizedRecord[], finalized: boolean): NormalizedRecord[] {
  const copy = [...records];
  copy.sort((a, b) => {
    if (finalized) {
      if (a.weight !== b.weight) return b.weight - a.weight;
    }
    if (a.updatedAt !== b.updatedAt) {
      return b.updatedAt.localeCompare(a.updatedAt);
    }
    return a.recordId.localeCompare(b.recordId);
  });
  return copy;
}

export function sortByFinalized(records: NormalizedRecord[]): NormalizedRecord[] {
  return sortRecords(records, true);
}
